import { useEffect, useMemo, useRef, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  IRecentlyAddedBook,
  IRecentlyAddedBooksGridFilter,
  IRecentlyAddedBooksGridFilterSchema,
} from "../api/types";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { formatDateTimeIST } from "utils/date";
import { useForm } from "react-hook-form";
import { IDropdownModel } from "common/models/IDropdownModel";
import { FilterOutlined } from "@ant-design/icons";
import { AutoCompleteElement } from "components/AutoComplete";
import RadioButtonGroup from "components/RadioButtonGroup";
import DatePickerElement from "components/DatePickerElement";
import { GetMaterialTypeValues } from "features/materialtype/api/hooks";
import { GetInstituteValues } from "features/institute/api/hooks";
import { GetDepartmentValuesByInstitute } from "features/department/api/hooks";
import { GetLanguageValues } from "features/language/api/hooks";
import useDashboardStore from "../store/DashboardStore";
import { GetRecentlyAddedBooks } from "../api/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import { AiFillFileExcel } from "react-icons/ai";
import api from "utils/api";
import dayjs from "dayjs";
import { ExportButton } from "components/ExportButton";

const RecentlyAddedBooksGrid = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [activePreset, setActivePreset] = useState<string>("all");

  const {
    recentlyAddedBooksPostModel,
    handleRecentlyAddedBooksFiltering,
    handleRecentlyAddedBooksPagination,
    handleRecentlyAddedBooksSorting,
  } = useDashboardStore();

  const { data: getBooksQuery, isFetching } = GetRecentlyAddedBooks(
    recentlyAddedBooksPostModel,
  );

  const methods = useForm<IRecentlyAddedBooksGridFilter>({
    mode: "all",
    resolver: zodResolver(IRecentlyAddedBooksGridFilterSchema),
    defaultValues: {
      materialTypeID: null,
      instituteID: null,
      departmentID: null,
      languageID: null,
      fromDate: null,
      toDate: null,
      searchText: null,
    },
  });

  const {
    register,
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = methods;
  const fromDate = watch("fromDate");
  const toDate = watch("toDate");

  // Auto-adjust toDate if fromDate is selected after it
  useEffect(() => {
    if (fromDate && toDate) {
      const from = dayjs(fromDate);
      const to = dayjs(toDate);
      if (from.isValid() && to.isValid() && from.isAfter(to, "day")) {
        setValue("toDate", fromDate);
      }
    }
  }, [fromDate, toDate, setValue]);
  const selectedInstituteID = watch("instituteID");

  // Dropdown options
  const { data: materialTypeQuery } = GetMaterialTypeValues();
  const { data: instituteQuery } = GetInstituteValues();
  const { data: departmentQuery } = GetDepartmentValuesByInstitute(
    selectedInstituteID || "",
  );
  const { data: languageQuery } = GetLanguageValues();

  const [materialTypeOptions, setMaterialTypeOptions] = useState<
    IDropdownModel[]
  >([]);
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>(
    [],
  );
  const [departmentOptions, setDepartmentOptions] = useState<IDropdownModel[]>(
    [],
  );
  const [languageOptions, setLanguageOptions] = useState<IDropdownModel[]>([]);

  useEffect(() => {
    setMaterialTypeOptions(materialTypeQuery?.apiData || []);
  }, [materialTypeQuery]);

  useEffect(() => {
    setInstituteOptions(instituteQuery?.apiData || []);
  }, [instituteQuery]);

  useEffect(() => {
    setDepartmentOptions(departmentQuery?.apiData || []);
  }, [departmentQuery]);

  useEffect(() => {
    setLanguageOptions(languageQuery?.apiData || []);
  }, [languageQuery]);

  // Reset department if institute changes
  useEffect(() => {
    if (!selectedInstituteID) {
      setValue("departmentID", null);
    }
  }, [selectedInstituteID, setValue]);

  const onSubmit = handleSubmit((data) => {
    handleRecentlyAddedBooksFiltering({
      ...data,
      materialTypeID: data.materialTypeID ? String(data.materialTypeID) : null,
      fromDate: data.fromDate
        ? dayjs(data.fromDate).format("YYYY-MM-DD")
        : null,
      toDate: data.toDate ? dayjs(data.toDate).format("YYYY-MM-DD") : null,
    });
  });

  const handlePresetClick = (presetKey: string) => {
    setActivePreset(presetKey);
    const currentValues = getValues();
    let fromDate: string | null = null;
    let toDate: string | null = null;

    if (presetKey === "last7Days") {
      fromDate = dayjs().subtract(7, "day").format("YYYY-MM-DD");
      toDate = dayjs().format("YYYY-MM-DD");
    } else if (presetKey === "last30Days") {
      fromDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");
      toDate = dayjs().format("YYYY-MM-DD");
    } else if (presetKey === "thisMonth") {
      fromDate = dayjs().startOf("month").format("YYYY-MM-DD");
      toDate = dayjs().endOf("month").format("YYYY-MM-DD");
    }

    setValue("fromDate", fromDate);
    setValue("toDate", toDate);

    const updatedFilter: IRecentlyAddedBooksGridFilter = {
      ...currentValues,
      fromDate,
      toDate,
    };
    handleRecentlyAddedBooksFiltering(updatedFilter);
  };

  const handleClear = () => {
    setActivePreset("all");
    const cleared: IRecentlyAddedBooksGridFilter = {
      materialTypeID: null,
      instituteID: null,
      departmentID: null,
      languageID: null,
      fromDate: null,
      toDate: null,
      searchText: null,
    };
    reset(cleared);
    handleRecentlyAddedBooksFiltering(cleared);
  };

  useEffect(() => {
    reset(recentlyAddedBooksPostModel.filterModel);
  }, [recentlyAddedBooksPostModel.filterModel, reset]);

  const rowCountRef = useRef(getBooksQuery?.apiData?.count || 0);
  const rowCount = useMemo(() => {
    if (getBooksQuery?.apiData?.count !== undefined) {
      rowCountRef.current = getBooksQuery?.apiData?.count;
    }
    return rowCountRef.current;
  }, [getBooksQuery?.apiData?.count]);

  const handleExport = () => {
    const filter = recentlyAddedBooksPostModel.filterModel;
    const exportPayload = {
      materialTypeID: filter.materialTypeID || null,
      instituteID: filter.instituteID || null,
      departmentID: filter.departmentID || null,
      languageID: filter.languageID || null,
      fromDate: filter.fromDate ? dayjs(filter.fromDate).toDate() : null,
      toDate: filter.toDate ? dayjs(filter.toDate).toDate() : null,
      searchText: filter.searchText || null,
    };

    setExportLoading(true);
    api.downloadExcel(
      "api/Reports/recentlyAddedBooksExports",
      "RecentlyAddedBooksExport",
      exportPayload,
      setExportLoading,
    );
  };

  const columns: GridColDef<IRecentlyAddedBook>[] = useMemo(
    () => [
       {
        field: "created",
        headerName: "Added Date",
        minWidth: 130,
        flex: 0.6,
        renderCell: (params) =>
          params.value ? formatDateTimeIST(params.value) : "-",
      },
      {
        field: "title",
        headerName: "Book Title",
        minWidth: 240,
        flex: 1.5,
        renderCell: (params) => params.value || "-",
      },
       {
        field: "materialTypeName",
        headerName: "Material Type",
        minWidth: 120,
        flex: 0.7,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "accessionCount",
        headerName: "Acc. Count",
        minWidth: 100,
        flex: 0.6,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Chip
            size="small"
            color="primary"
            variant="light"
            label={params.value ?? 0}
            sx={{ fontWeight: "bold" }}
          />
        ),
      },
      {
        field: "accessionNoCSV",
        headerName: "Accession Numbers",
        minWidth: 200,
        flex: 1.3,
        renderCell: (params) => {
          const accs = params.value || "-";
          return (
            <Tooltip title={accs} placement="top-start">
              <Typography
                // variant="caption"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.3,
                }}
              >
                {accs}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "authorNameCSV",
        headerName: "Author(s)",
        minWidth: 180,
        flex: 1.1,
        renderCell: (params) => {
          const authors = params.value || params.row.primaryAuthorName || "-";
          return (
            <Tooltip title={authors} placement="top-start">
              <Typography
                // variant="body2"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {authors}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "classificationCode",
        headerName: "Class No.",
        minWidth: 100,
        flex: 0.6,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => params.value || "-",
      },
      {
        field: "languageName",
        headerName: "Language",
        minWidth: 110,
        flex: 0.7,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "edition",
        headerName: "Edition",
        minWidth: 100,
        flex: 0.6,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "publicationName",
        headerName: "Publisher",
        minWidth: 160,
        flex: 1,
        renderCell: (params) => {
          const pub = params.value || "-";
          return (
            <Tooltip title={pub} placement="top-start">
              <Typography
                // variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {pub}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "isbn",
        headerName: "ISBN",
        minWidth: 140,
        flex: 0.8,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "publicationYear",
        headerName: "Year",
        minWidth: 80,
        flex: 0.5,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => params.value || "-",
      },
      {
        field: "pages",
        headerName: "Pages / Physical",
        minWidth: 140,
        flex: 0.8,
        renderCell: (params) => {
          const pages = params.row.pages;
          const size = params.row.size;
          const details = [
            pages ? `${pages}p` : null,
            size ? `Size: ${size}` : null,
          ]
            .filter(Boolean)
            .join(", ");
          return details || "-";
        },
      },
      {
        field: "classificationName",
        headerName: "Subject / Classification",
        minWidth: 160,
        flex: 1,
        renderCell: (params) => params.value || "-",
      }
    ],
    [],
  );

  return (
    <>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Grid
          container
          sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
        >
          <Grid item>
            <Typography component="div" variant="h3" marginBlockEnd={2}>
              Recently Added Books/Accessions
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2 }} />

        {/* Quick Filter Preset Chips */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}
          >
            Quick Filters:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            <Chip
              label="All"
              clickable
              color={activePreset === "all" ? "primary" : "default"}
              variant={activePreset === "all" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("all")}
            />
            <Chip
              label="Added Last 7 Days"
              clickable
              color={activePreset === "last7Days" ? "primary" : "default"}
              variant={activePreset === "last7Days" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("last7Days")}
            />
            <Chip
              label="Added Last 30 Days"
              clickable
              color={activePreset === "last30Days" ? "primary" : "default"}
              variant={activePreset === "last30Days" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("last30Days")}
            />
            <Chip
              label="Added This Month"
              clickable
              color={activePreset === "thisMonth" ? "primary" : "default"}
              variant={activePreset === "thisMonth" ? "filled" : "outlined"}
              onClick={() => handlePresetClick("thisMonth")}
            />
          </Stack>
        </Box>

        <Box component="form" noValidate sx={{ mt: 1 }} onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RadioButtonGroup
                row
                control={control}
                name="materialTypeID"
                label="Material Type"
                options={materialTypeOptions}
                labelKey="name"
                valueKey="id"
                emptyOptionLabel="All"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                fullWidth
                {...register("searchText")}
                label="Search (Title / ISBN / Author / Acc No)"
                placeholder="Enter search keyword"
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <AutoCompleteElement
                options={languageOptions}
                control={control}
                name="languageID"
                label="Language"
                placeholder="Select Language"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <AutoCompleteElement
                options={instituteOptions}
                control={control}
                name="instituteID"
                label="Institute"
                placeholder="Select Institute"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <AutoCompleteElement
                options={departmentOptions}
                control={control}
                name="departmentID"
                label="Department"
                placeholder="Select Department"
                disabled={!selectedInstituteID}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DatePickerElement
                control={control}
                name="fromDate"
                label="From Date"
                format="DD-MM-YYYY"
                maxDate={
                  toDate && dayjs(toDate).isValid() ? dayjs(toDate) : undefined
                }
                sx={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DatePickerElement
                control={control}
                name="toDate"
                label="To Date"
                format="DD-MM-YYYY"
                minDate={
                  fromDate && dayjs(fromDate).isValid()
                    ? dayjs(fromDate)
                    : undefined
                }
                sx={{ width: "100%" }}
              />
            </Grid>
          </Grid>

          <Button
            title="Filter"
            type="submit"
            variant="contained"
            sx={{ mt: 2, mr: 1 }}
            disabled={isSubmitting}
            startIcon={<FilterOutlined />}
          >
            Filter
          </Button>
          <Button
            title="Clear"
            type="reset"
            variant="contained"
            color="secondary"
            sx={{ mt: 2 }}
            onClick={handleClear}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ mt: 2, px: 2, py: 2 }}>
        <Grid
          container
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            gap: 1,
          }}
        >
          <Grid item>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Total Accessions: {rowCount}
            </Typography>
          </Grid>
          <Grid item>
            <ExportButton
              groupName="RecentlyAddedBooks"
              fileName="RecentlyAddedBooksExport"
              reportTitle="Recently Added Books Report"
              params={() => ({
                materialTypeID:
                  recentlyAddedBooksPostModel.filterModel.materialTypeID ||
                  null,
                instituteID:
                  recentlyAddedBooksPostModel.filterModel.instituteID || null,
                departmentID:
                  recentlyAddedBooksPostModel.filterModel.departmentID || null,
                languageID:
                  recentlyAddedBooksPostModel.filterModel.languageID || null,
                fromDate: recentlyAddedBooksPostModel.filterModel.fromDate
                  ? dayjs(
                      recentlyAddedBooksPostModel.filterModel.fromDate,
                    ).toDate()
                  : null,
                toDate: recentlyAddedBooksPostModel.filterModel.toDate
                  ? dayjs(
                      recentlyAddedBooksPostModel.filterModel.toDate,
                    ).toDate()
                  : null,
                searchText:
                  recentlyAddedBooksPostModel.filterModel.searchText || null,
              })}
              buttonText="Export"
            />
          </Grid>
        </Grid>

        <Grid container sx={{ height: 520 }}>
          <StyledDataGrid
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "wrap",
                fontWeight: 600,
                lineHeight: "normal",
                fontSize: "13px",
              },
              "& .MuiDataGrid-cell": {
                whiteSpace: "wrap",
                alignContent: "center",
                fontSize: 14,
              },
            }}
            density="compact"
            getRowId={(item) => item.materialID}
            columns={columns}
            pageSizeOptions={[10, 20, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  ...recentlyAddedBooksPostModel.pagingModel,
                },
              },
            }}
            paginationModel={recentlyAddedBooksPostModel.pagingModel}
            sortModel={recentlyAddedBooksPostModel.sortModel}
            onSortModelChange={handleRecentlyAddedBooksSorting}
            onPaginationModelChange={handleRecentlyAddedBooksPagination}
            getRowHeight={() => "auto"}
            paginationMode="server"
            sortingMode="server"
            rows={getBooksQuery?.apiData?.data || []}
            rowCount={rowCount}
            disableColumnFilter
            disableColumnSorting={false}
            loading={isFetching}
          />
        </Grid>
      </Paper>
    </>
  );
};

export default RecentlyAddedBooksGrid;
